import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { buildBreadcrumbSchema, type BreadcrumbItem } from '@/lib/breadcrumbs'

interface SEOProps {
    title?: string
    description?: string
    keywords?: string
    canonicalPath?: string // Override auto-generated canonical URL
    noIndex?: boolean
    ogTitle?: string
    ogDescription?: string
    ogImage?: string
    ogType?: 'website' | 'article'
    jsonLd?: Record<string, any> // Basic JSON-LD support
    /** Home is implicit — start from the first level under it, e.g. [{ name: 'Blog', path: '/blogs' }, { name: postTitle }] (omit `path` on the current page). */
    breadcrumbs?: BreadcrumbItem[]
}

const BASE_URL = 'https://www.planwise.social'

export function useSEO({
    title,
    description,
    keywords,
    canonicalPath,
    noIndex = false,
    ogTitle,
    ogDescription,
    ogImage,
    ogType = 'website',
    jsonLd,
    breadcrumbs
}: SEOProps = {}) {
    const location = useLocation()

    useEffect(() => {
        // Helper to set or create meta tag
        const setMetaTag = (name: string, content: string, isProperty = false) => {
            const attrName = isProperty ? 'property' : 'name'
            let element = document.querySelector(`meta[${attrName}="${name}"]`) as HTMLMetaElement

            if (!element) {
                element = document.createElement('meta')
                element.setAttribute(attrName, name)
                document.head.appendChild(element)
            }
            element.setAttribute('content', content)
        }

        // Helper to set or create link tag
        const setLinkTag = (rel: string, href: string) => {
            let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement

            if (!element) {
                element = document.createElement('link')
                element.setAttribute('rel', rel)
                document.head.appendChild(element)
            }
            element.setAttribute('href', href)
        }

        // Set title
        if (title) {
            document.title = title
        }

        // Set canonical URL - removes trailing slashes for consistency
        const cleanPath = (canonicalPath || location.pathname).replace(/\/$/, '') || '/'
        const cleanUrl = `${BASE_URL}${cleanPath}`

        // Each language needs its own URL for hreflang to mean anything, so
        // '?lng=' pins one (see the querystring detector in src/i18n.ts).
        const requestedLang = new URLSearchParams(location.search).get('lng')
        const pinnedLang = requestedLang === 'id' || requestedLang === 'en' ? requestedLang : null

        // canonicalPath may already carry its own query string (e.g. blog
        // pagination's '?page=2') — join with '&' rather than assuming '?'.
        const lngSeparator = cleanUrl.includes('?') ? '&' : '?'

        // The canonical MUST be self-referential per language. Pointing
        // '?lng=id' back at the bare URL would make every hreflang target
        // non-canonical, and Google discards a cluster whose targets are.
        const canonicalUrl = pinnedLang ? `${cleanUrl}${lngSeparator}lng=${pinnedLang}` : cleanUrl
        setLinkTag('canonical', canonicalUrl)

        // hreflang set: one entry per language plus x-default, identical on all
        // three URLs so every pair links back reciprocally. Rebuilt from scratch
        // each time so a client-side navigation cannot leave a stale entry behind.
        document
            .querySelectorAll('link[rel="alternate"][hreflang]')
            .forEach((element) => element.remove())

        const alternates: [string, string][] = [
            ['en', `${cleanUrl}${lngSeparator}lng=en`],
            ['id', `${cleanUrl}${lngSeparator}lng=id`],
            // x-default is the un-pinned URL, which auto-detects the reader's language.
            ['x-default', cleanUrl],
        ]

        for (const [hreflang, href] of alternates) {
            const link = document.createElement('link')
            link.setAttribute('rel', 'alternate')
            link.setAttribute('hreflang', hreflang)
            link.setAttribute('href', href)
            document.head.appendChild(link)
        }

        // Set meta description
        if (description) {
            setMetaTag('description', description)
        }

        // Set keywords
        if (keywords) {
            setMetaTag('keywords', keywords)
        }

        // Set robots
        if (noIndex) {
            setMetaTag('robots', 'noindex, nofollow')
        } else {
            setMetaTag('robots', 'index, follow')
        }

        // Open Graph tags
        setMetaTag('og:url', canonicalUrl, true)
        if (ogTitle || title) {
            setMetaTag('og:title', ogTitle || title || 'PlanWise', true)
        }
        if (ogDescription || description) {
            setMetaTag('og:description', ogDescription || description || '', true)
        }
        if (ogImage) {
            setMetaTag('og:image', ogImage, true)
        }
        setMetaTag('og:type', ogType, true)

        // Twitter tags
        if (ogTitle || title) {
            setMetaTag('twitter:title', ogTitle || title || 'PlanWise')
        }
        if (ogDescription || description) {
            setMetaTag('twitter:description', ogDescription || description || '')
        }

        // Set JSON-LD. Scoped to data-schema="page" so this only ever touches
        // schema this hook itself created — a bare `script[type="application/
        // ld+json"]` selector would grab and overwrite the site-wide
        // SoftwareApplication/Organization scripts from index.html instead of
        // adding to them (this happened in production: every page passing
        // jsonLd here clobbered the global brand schema).
        const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0 ? buildBreadcrumbSchema(breadcrumbs) : undefined
        const existingPageSchema = document.querySelector('script[data-schema="page"]')
        if (jsonLd || breadcrumbSchema) {
            let script = existingPageSchema as HTMLScriptElement
            if (!script) {
                script = document.createElement('script')
                script.setAttribute('type', 'application/ld+json')
                script.setAttribute('data-schema', 'page')
                document.head.appendChild(script)
            }

            let finalSchema: Record<string, unknown>
            if (jsonLd && breadcrumbSchema) {
                // '@graph' combines a page's own schema (BlogPosting, FAQPage,
                // whatever) with the breadcrumb in one script tag rather than
                // needing a second one — one '@context' at the root covers both.
                // Some pages already pass their own '@graph' array (multiple
                // nodes, e.g. SoftwareApplication + HowTo) — flatten into that
                // rather than nesting a '@graph' inside a '@graph', which
                // isn't valid JSON-LD.
                const { '@context': _pageContext, '@graph': existingGraph, ...jsonLdNode } = jsonLd as Record<string, unknown> & { '@graph'?: unknown[] }
                const graphNodes = Array.isArray(existingGraph) ? existingGraph : [jsonLdNode]
                finalSchema = { '@context': 'https://schema.org', '@graph': [...graphNodes, breadcrumbSchema] }
            } else if (jsonLd) {
                finalSchema = jsonLd
            } else {
                finalSchema = { '@context': 'https://schema.org', ...breadcrumbSchema }
            }

            script.textContent = JSON.stringify(finalSchema)
        } else if (existingPageSchema) {
            // A client-side navigation to a page with no jsonLd/breadcrumbs of
            // its own must not leave the previous page's schema behind.
            existingPageSchema.remove()
        }

    }, [title, description, keywords, canonicalPath, noIndex, ogTitle, ogDescription, ogImage, ogType, location.pathname, location.search, jsonLd, breadcrumbs])
}

export default useSEO
